// ============================================================================
// CommandPalette.jsx — Global Cmd+K / Ctrl+K search + command launcher
// Searches projects, jumps to pages, opens modals, one keystroke away
// ============================================================================

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || ''

// Static commands — always available
const STATIC_COMMANDS = [
  { id: 'nav-dashboard',   type: 'nav', icon: '🏠',  title: 'Dashboard',       subtitle: 'Home',                         keywords: 'home main',    path: '/dashboard' },
  { id: 'nav-workspace',   type: 'nav', icon: '✨',  title: 'New Project',     subtitle: 'Start creating',               keywords: 'new create',   path: '/workspace' },
  { id: 'nav-gallery',     type: 'nav', icon: '🎨',  title: 'Community Gallery', subtitle: 'Browse designs',             keywords: 'browse inspiration community', path: '/gallery' },
  { id: 'nav-marketplace', type: 'nav', icon: '🛒',  title: 'Marketplace',     subtitle: 'Buy & sell designs',           keywords: 'buy sell shop',  path: '/marketplace' },
  { id: 'nav-pricing',     type: 'nav', icon: '💜',  title: 'Pricing & Plans', subtitle: 'Upgrade your plan',            keywords: 'upgrade buy credits subscription', path: '/pricing' },
  { id: 'nav-profile',     type: 'nav', icon: '👤',  title: 'My Profile',      subtitle: 'View your profile',            keywords: 'account me',     path: '/profile' },

  // Craft quick-starts (pre-fill a prompt)
  { id: 'start-cross-stitch', type: 'start', icon: '🧵', title: 'Start: Cross-Stitch Pattern',   subtitle: 'Grid chart with DMC colors',    keywords: 'cross stitch aida',          prompt: 'Create a cross-stitch pattern', craft: 'cross-stitch' },
  { id: 'start-sewing',       type: 'start', icon: '✂️', title: 'Start: Sewing Pattern',         subtitle: 'Garment pattern with pieces',   keywords: 'sew garment clothing',       prompt: 'I want to sew',                 craft: 'sewing' },
  { id: 'start-perler',       type: 'start', icon: '🟦', title: 'Start: Perler Beads',           subtitle: 'Pixel art with bead palette',   keywords: 'perler fuse hama beads',     prompt: 'Create a perler bead pattern',  craft: 'perler-beads' },
  { id: 'start-cricut',       type: 'start', icon: '✂️', title: 'Start: Cricut / Vinyl',         subtitle: 'SVG-ready vinyl design',        keywords: 'cricut vinyl svg cut',       prompt: 'Create a vinyl decal design',   craft: 'vinyl-decal' },
  { id: 'start-quilting',     type: 'start', icon: '🧩', title: 'Start: Quilt Block',            subtitle: 'Patchwork block pattern',       keywords: 'quilt patchwork block',      prompt: 'Create a quilt block pattern',  craft: 'quilting' },
  { id: 'start-crochet',      type: 'start', icon: '🧶', title: 'Start: Crochet',                subtitle: 'Crochet pattern with steps',    keywords: 'crochet yarn amigurumi',     prompt: 'Create a crochet pattern',      craft: 'crochet' },
  { id: 'start-coloring',     type: 'start', icon: '🖍️', title: 'Start: Coloring Page',          subtitle: 'Black-line art to color',       keywords: 'coloring page lineart',      prompt: 'Create a coloring page',        craft: 'coloring-page' },
  { id: 'start-tshirt',       type: 'start', icon: '👕', title: 'Start: T-Shirt Design',         subtitle: 'Print-ready apparel graphic',   keywords: 'tshirt t-shirt apparel shirt', prompt: 'Design a t-shirt',            craft: 't-shirt-design' },
]

function useKey(key, handler, opts = {}) {
  useEffect(() => {
    const onKey = (e) => {
      const wantsCmd = opts.cmd ?? false
      const wantsShift = opts.shift ?? false
      if (wantsCmd && !(e.metaKey || e.ctrlKey)) return
      if (wantsShift && !e.shiftKey) return
      if (e.key.toLowerCase() !== key.toLowerCase()) return
      if (opts.preventDefault) e.preventDefault()
      handler(e)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [key, handler, opts.cmd, opts.shift, opts.preventDefault])
}

export default function CommandPalette({ userEmail }) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [projects, setProjects] = useState([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef(null)

  // Cmd+K / Ctrl+K toggles
  useKey('k', () => setOpen(o => !o), { cmd: true, preventDefault: true })
  // Escape closes
  useKey('Escape', () => setOpen(false))

  // Load user's projects when opening
  useEffect(() => {
    if (!open || !userEmail) return
    fetch(`${API_URL}/projects?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(data => setProjects(data.projects || []))
      .catch(() => setProjects([]))
  }, [open, userEmail])

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Build searchable results
  const q = query.toLowerCase().trim()
  const projectResults = projects
    .filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.type || '').toLowerCase().includes(q))
    .slice(0, 6)
    .map(p => ({
      id: `project-${p.id}`,
      type: 'project',
      icon: '📁',
      title: p.name || 'Untitled',
      subtitle: `Updated ${formatRelativeTime(p.updated_at)}`,
      path: `/workspace?project=${p.id}`,
      thumbnail: p.images?.[0],
    }))

  const commandResults = STATIC_COMMANDS.filter(c => {
    if (!q) return true
    const haystack = `${c.title} ${c.subtitle} ${c.keywords}`.toLowerCase()
    return haystack.includes(q)
  })

  // Assemble final list
  const results = q
    ? [...projectResults, ...commandResults]
    : [...commandResults, ...projectResults]

  const handleSelect = useCallback((result) => {
    if (!result) return
    setOpen(false)
    if (result.type === 'project') {
      navigate(result.path)
    } else if (result.type === 'nav') {
      navigate(result.path)
    } else if (result.type === 'start') {
      const params = new URLSearchParams({ startPrompt: result.prompt, startCraft: result.craft })
      navigate(`/workspace?${params.toString()}`)
    }
  }, [navigate])

  // Keyboard navigation
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(i => Math.min(i + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(i => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        handleSelect(results[selectedIndex])
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, results, selectedIndex, handleSelect])

  // Reset index when results change
  useEffect(() => { setSelectedIndex(0) }, [query])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-700">
          <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search projects, crafts, or pages..."
            className="flex-1 bg-transparent outline-none text-slate-900 dark:text-white placeholder-slate-400 text-base"
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono text-slate-400 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto py-2">
          {results.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              No results for "{query}"
            </div>
          ) : (
            results.map((r, i) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                onMouseEnter={() => setSelectedIndex(i)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === selectedIndex
                    ? 'bg-zenna-violet/10 text-zenna-violet'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
              >
                {r.thumbnail ? (
                  <img src={r.thumbnail} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 flex items-center justify-center text-xl shrink-0">{r.icon}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 truncate">{r.subtitle}</div>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                  {r.type === 'project' ? 'Project' : r.type === 'start' ? 'New' : 'Go to'}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between text-[10px] text-slate-400">
          <div className="flex items-center gap-3">
            <span><kbd className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">↑↓</kbd> Navigate</span>
            <span><kbd className="font-mono bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">↵</kbd> Select</span>
          </div>
          <span>Zenna Command Palette</span>
        </div>
      </div>
    </div>
  )
}

function formatRelativeTime(date) {
  if (!date) return ''
  const s = Math.floor((Date.now() - new Date(date)) / 1000)
  if (s < 60) return 'just now'
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}
